import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Pagination from "../utils/Pagination";
import ExportCSVButton from '../utils/ExportCSVButton';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faFilter,
  faTimes,
  faSpinner,
  faCalendar
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import logo from "../buckland.png";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getTranslation } from "../utils/i18n";
import {FormatLocalDateStringBorder} from "../utils/dateFormat"
import {
  faBuilding,
  faIndustry,
  faUserTie,
  faPlane,
  faKey,
  faMapMarkerAlt,
  faInfoCircle,
  faClipboardCheck,
  faSearch,        
  faCalendarAlt,   
  faFileExport,
  faDollarSign,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import { DateRangePicker } from "react-date-range";

const CustomerReportWeeklyOperations = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState("en");
  const { logout, customerNumbers } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [allItems, setAllItems] = useState([]);
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "" });
  const [globalSearch, setGlobalSearch] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Para controlar si se usan fechas automáticas o manuales
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);
  
  // Estado temporal para el DateRangePicker (para evitar llamadas automáticas)
  const [tempDateRange, setTempDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);
  
  const [filters, setFilters] = useState({
    CLIENTE: [],
    PLAZA: [],
    OPERACION: [],
    ADUANA: [],
    PATENTE: [],
    PLANTA: [],
  });
  const [selectedRFC, setSelectedRFC] = useState("HEL850315SC5");

  // Lista de RFCs disponibles
  const availableRFCs = [
    { value: "HEL850315SC5", label: "HEL850315SC5 - HELLAMEX" },
    { value: "ECL831108M87", label: "ECL831108M87 - 2244" },
    { value: "EOP640608I13", label: "EOP640608I13 - HAM" },
    { value: "MDM9305078I0", label: "MDM9305078I0 - MDM02" },
    { value: "TTO000904LI5", label: "TTO000904LI5 - HMT" },
  ];
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // getMonth es 0-indexed
  const dd = String(today.getDate()).padStart(2, '0');
  const formattedDate = `${yyyy}-${mm}-${dd}`;

  // Función para calcular las fechas de la semana
  const getWeekDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();

    let targetWeek;
    if (dayOfWeek === 1) {
      targetWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else {
      targetWeek = today;
    }

    const monday = new Date(targetWeek);
    const mondayDiff = targetWeek.getDay() === 0 ? 6 : targetWeek.getDay() - 1;
    monday.setDate(targetWeek.getDate() - mondayDiff);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      fechaInicio: formatDate(monday),
      fechaFin: formatDate(sunday)
    };
  };

  // Función para obtener las fechas que se deben usar (automáticas o manuales)
  const getEffectiveDateRange = () => {
    if (useCustomDateRange && dateRange[0].startDate && dateRange[0].endDate) {
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      return {
        fechaInicio: formatDate(dateRange[0].startDate),
        fechaFin: formatDate(dateRange[0].endDate)
      };
    }

    return getWeekDates();
  };


  // Campos relevantes para el reporte general basado en EBooking
  const keyMap = {
   "Cliente":"CLIENTE",
   "Plaza":"PLAZA",
   "Operacion":"OPERACION",
   "Fecha":"Cliente",
   "Aduana":"FECHA",
   "Patente":"PATENTE",
   "Pedimento":"PEDIMENTO",
   "Referencia":"REFERENCIA",
   "Suma":"SUMA",
   "Impuestos Pagados":"IMPUESTOS_PAGADOS",
   "Planta":"PLANTA",
  };

  const csvData = items.map(item => {
    const row = {};
    Object.entries(keyMap).forEach(([header, key]) => {
      row[header] = item[key] ?? ""; // Usa el encabezado visible como clave
    });
    return row;
  });

  const fieldIcons = {
    CLIENTE: faBuilding,
    PLAZA: faMapMarkerAlt,
    OPERACION: faGear,
    FECHA: faCalendarAlt,
    ADUANA: faMapMarkerAlt,
    PATENTE: faKey,
    PEDIMENTO: faFileExport,
    REFERENCIA: faInfoCircle,
    SUMA: faDollarSign,
    IMPUESTOS_PAGADOS: faClipboardCheck,
    PLANTA: faBuilding,
  };

  const parseFecha = (fechaString) => {
    if (!fechaString || fechaString === '-') return null;
    
    if (fechaString.includes('/')) {
      const parts = fechaString.split('/');
      
      if (parts.length === 3) {
        const month = parseInt(parts[0]) - 1;
        const day = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        
        if (year > 1900 && month >= 0 && month < 12 && day >= 1 && day <= 31) {
          const resultDate = new Date(year, month, day);
          return resultDate;
        }
      }
    }
    
    return null;
  };

  const selectOptions = useMemo(() => {
    const fields = [
      "CLIENTE",
      "PLAZA", 
      "OPERACION",
      "ADUANA",
      "PATENTE",
      "PLANTA",
    ];

    return fields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: [...new Set(allItems.map((item) => item[field]))]
          .filter(value => value && value !== '-')
          .map((value) => ({ value, label: value }))
      }),
      {}
    );
  }, [allItems]);

  // MODIFICADO: Solo cargar datos al inicio con el RFC seleccionado
  useEffect(() => {
    fetchData();
  }, [selectedRFC]); // Solo cuando cambia el RFC

  // Efecto para filtrar y paginar datos
  useEffect(() => {
    let filteredItems = allItems.filter((item) => {
      // Aplicar condiciones de filtro
      const passesFilters = Object.entries(filters).every(
        ([key, selectedOptions]) =>
          selectedOptions.length === 0 ||
          selectedOptions.some((option) => option.value === item[key])
      );

      // Aplicar búsqueda global
      const passesGlobalSearch = !globalSearch || 
        Object.values(item).some(value => 
          value?.toString().toLowerCase().includes(globalSearch.toLowerCase())
        );

      return passesFilters && passesGlobalSearch;
    });

    // Aplicar ordenamiento
    if (sortConfig.key) {
      filteredItems = sortItems(filteredItems);
    }

    setTotalPages(Math.ceil(filteredItems.length / pageSize));

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filteredItems.slice(startIndex, endIndex);

    setItems(paginatedItems);
  }, [allItems, filters, globalSearch, sortConfig, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    if (items.length) {
      let sortedItems = sortItems([...items]);
      setItems(sortedItems);
    }
  }, [sortConfig]);

  const toggleFilters = () => {
    setFilterVisible(!filterVisible);
  };

  // NUEVO: Función para manejar cambios temporales en el DateRangePicker
  const handleTempDateRangeChange = (ranges) => {
    setTempDateRange([ranges.selection]);
  };

  // NUEVO: Función para aplicar el rango de fechas seleccionado
  const applyDateRange = () => {
    if (tempDateRange[0].startDate && tempDateRange[0].endDate) {
      setDateRange([...tempDateRange]);
      setUseCustomDateRange(true);
      setShowDatePicker(false);
      
      // Aquí llamamos a fetchData para cargar los datos con el nuevo rango
      setTimeout(() => {
        fetchDataWithDateRange(tempDateRange[0].startDate, tempDateRange[0].endDate);
      }, 100);
    }
  };

  // NUEVO: Función específica para cargar datos con un rango de fechas
  const fetchDataWithDateRange = async (startDate, endDate) => {
    setIsLoading(true);
    setError(null);
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      setError("Access token is not available");
      setIsLoading(false);
      return;
    }

    const allRFCs = availableRFCs.map(rfc => rfc.value);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const params = {
      fechaInicio: formatDate(startDate),
      fechaFin: formatDate(endDate),
      customer_rfc: allRFCs
    };

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/customer/reports/paid/weekly`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          }, 
          params: params 
        }
      );

      const transformedData = response.data.map((item, index) => {
        const formatFecha = (fechaData) => {
          if (!fechaData) return '-';
          if (typeof fechaData === 'object' && fechaData.value) {
            return FormatLocalDateStringBorder(fechaData.value);
          }
          return FormatLocalDateStringBorder(fechaData);
        };

        return {
          id: index,
          CLIENTE: item.RAZONSOCIALPEDIMENTO || '-',
          PLAZA: item.ADUANADESCRIPCION || '-',
          OPERACION: item.TIPO_OPERACION || '-',
          FECHA: formatFecha(item.FECHAPAGO),
          ADUANA: item.ADUANA || '-',
          PATENTE: item.PATENTE || '-',
          PEDIMENTO: item.PEDIMENTO || '-',
          REFERENCIA: item.REFERENCIA_PEDIMENTO || '-',
          SUMA: item.TOTAL_IMPUESTOS_PEDIMENTO || '-',
          IMPUESTOS_PAGADOS: item.BANCO_PEDIMENTO || '-',
          PLANTA: determinePlanta(item.RFCCLIENTEPEDIMENTO, item.BODDESTINO),
        };
      });

      setAllItems(transformedData);
      setTotalPages(Math.ceil(transformedData.length / pageSize));
      setIsLoading(false);
    } catch (error) {
      setError("Failed to fetch data");
      setIsLoading(false);
      console.error("Failed to fetch data:", error);
    }
  };

  // Función para limpiar el filtro de fechas
  const clearDateFilter = () => {
    setDateRange([{
      startDate: null,
      endDate: null,
      key: "selection",
    }]);
    setTempDateRange([{
      startDate: null,
      endDate: null,
      key: "selection",
    }]);
    setUseCustomDateRange(false);
    setShowDatePicker(false);
    
    // Recargar datos automáticos
    fetchData();
  };

  // Función para contar filtros de fecha activos
  const getActiveDateFilterCount = () => {
    return useCustomDateRange && dateRange[0].startDate && dateRange[0].endDate ? 1 : 0;
  };

  // Actualizar clearAllFilters
  const clearAllFilters = () => {
    setFilters({
      CLIENTE: [],
      PLAZA: [],
      OPERACION: [],
      ADUANA: [],
      PATENTE: [],
      PLANTA: [],
    });
    setGlobalSearch("");
    clearDateFilter();
  };

  // Función para renderizar el tag del filtro de fecha
  const renderDateFilterTag = () => {
    if (!useCustomDateRange || !dateRange[0].startDate || !dateRange[0].endDate) {
      return null;
    }

    const { startDate, endDate } = dateRange[0];

    const formatDate = (date) => {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
        <FontAwesomeIcon icon={faCalendar} className="text-xs" />
        <span>{formatDate(startDate)} - {formatDate(endDate)}</span>
        <button
          onClick={clearDateFilter}
          className="ml-1 text-green-600 hover:text-green-800"
        >
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
        </button>
      </span>
    );
  };

  const renderHeader = () => (
    <div
      className="w-full flex flex-col px-2 md:px-4 py-2 md:py-4"
      style={{ backgroundColor: "#1A2654" }}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex items-center">
            <img src={logo} alt="Company Logo" className="h-16 mr-2" />
          </div>
        </div>
        <div className="ml-auto flex items-center">
          <h1 className="text-xl font-semibold text-white mr-4">
            Weekly Report
            {(() => {
              const effectiveDates = getEffectiveDateRange();
              return (
                <span className="text-sm font-normal block">
                  ({effectiveDates.fechaInicio} to {effectiveDates.fechaFin})
                  {useCustomDateRange && <span className="text-yellow-300"> - Custom Range</span>}
                </span>
              );
            })()}
          </h1>
          
          <button
            onClick={handleLogout}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {getTranslation(language, "logout")}
          </button>
          <select
            onChange={(e) => setLanguage(e.target.value)}
            value={language}
            className="ml-4 bg-gray-200 border border-gray-400 rounded-md py-1 px-2"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderSelectFilters = () => {
    const getActiveFiltersCount = () => {
      const regularFilters = Object.values(filters).reduce((count, filterArray) => count + filterArray.length, 0);
      const dateFilters = getActiveDateFilterCount();
      return regularFilters + dateFilters;
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1 relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                placeholder="Search clients, references, pedimentos..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={toggleFilters}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${filterVisible
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <FontAwesomeIcon icon={faFilter} className="text-sm" />
                <span>Filters</span>
                {getActiveFiltersCount() > 0 && (
                  <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>

              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`font-medium py-2 px-4 rounded flex items-center ${
                  useCustomDateRange 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                {showDatePicker ? "Hide Date Range" : 
                  useCustomDateRange ? "Custom Date Range" : "Select Date Range"}
              </button>

              {useCustomDateRange && (
                <button
                  onClick={clearDateFilter}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded flex items-center"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  Reset to Auto
                </button>
              )}
            </div>
          </div>

          {/* MODIFICADO: DateRangePicker con botones de control */}
          {showDatePicker && (
            <div className="relative">
              <div className="absolute right-0 z-50 mt-1">
                <div className="border border-gray-300 rounded-lg shadow-md bg-white">
                  <DateRangePicker
                    ranges={tempDateRange}
                    onChange={handleTempDateRangeChange}
                    maxDate={new Date()}
                    dateDisplayFormat="MM/dd/yyyy"
                    rangeColors={['#3b82f6']}
                    showDateDisplay={false}
                  />
                  {/* NUEVO: Botones de control */}
                  <div className="flex justify-between items-center p-3 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setTempDateRange([{
                            startDate: null,
                            endDate: null,
                            key: "selection",
                          }]);
                        }}
                        className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Clear
                      </button>
                      <button
                        onClick={applyDateRange}
                        disabled={!tempDateRange[0].startDate || !tempDateRange[0].endDate}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {filterVisible && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                  <FontAwesomeIcon icon={faFilter} className="text-blue-600" />
                  <span>Advanced Filters</span>
                </h3>
                {getActiveFiltersCount() > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                {[
                  { name: "CLIENTE", label: "Customer" },      
                   { name: "PLANTA", label: "Planta" },
                  { name: "OPERACION", label: "Operation Type" },
                  { name: "PLAZA", label: "Entry Point Name" },
                  { name: "ADUANA", label: "Entry Point" },
                  { name: "PATENTE", label: "Customs Broker License" },
          
                ].map(({ name, label }) => (
                  <div className="filter-group" key={name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-2">
                      <FontAwesomeIcon icon={fieldIcons[name]} className="text-blue-600 text-xs md:text-sm" />
                      <span>{label}</span>
                    </label>
                    <Select
                      isMulti
                      isClearable
                      name={name}
                      options={selectOptions[name]}
                      className="basic-multi-select text-sm"
                      classNamePrefix="select-filter"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '30px',
                          height: '30px',
                          fontSize: '12px',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          height: '30px',
                          padding: '0 6px',
                        }),
                        input: (base) => ({
                          ...base,
                          margin: '0px',
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          height: '30px',
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                      onChange={(selectedOptions) =>
                        setFilters((prev) => ({
                          ...prev,
                          [name]: selectedOptions || [],
                        }))
                      }
                      value={filters[name]}
                      placeholder={`Filter by ${label}`}
                    />
                  </div>
                ))}
              </div>

              {getActiveFiltersCount() > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(filters).map(([key, selectedOptions]) =>
                    selectedOptions.map((item) => (
                      <span
                        key={`${key}-${item.value}`}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        <span className="capitalize">{key}:</span>
                        <span>{item.label}</span>
                        <button
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              [key]: prev[key].filter((i) => i.value !== item.value),
                            }))
                          }
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                      </span>
                    ))
                  )}
                  
                  {renderDateFilterTag()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const sortItems = (itemsToSort) => {
    const { key, direction } = sortConfig;
    if (!key) return itemsToSort;

    return [...itemsToSort].sort((a, b) => {
      const valA = a[key] ?? "";
      const valB = b[key] ?? "";

      if (valA < valB) return direction === "ascending" ? -1 : 1;
      if (valA > valB) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else {
      direction = "ascending";
    }
    setSortConfig({ key, direction });
  };

  const determinePlanta = (rfc, bodDestino) => {
    if (rfc === 'ECL831108M87' && (!bodDestino || bodDestino === '')) {
      return '2244';
    }
    if (rfc === 'EOP640608I13' && (!bodDestino || bodDestino === '' )) {
      return 'HAM';
    }
    if (rfc === 'HEL850315SC5' && (!bodDestino || bodDestino === '' || bodDestino === 'null')) {
      return 'HELLAMEX';
    }
    if (rfc === 'MDM9305078I0' && (!bodDestino || bodDestino === '')) {
      return 'MDM02';
    }
    if (rfc === 'TTO000904LI5' && (!bodDestino || bodDestino === '')) {
      return 'HMT';
    }
    return bodDestino || '-';
  };

  // fetchData con las fechas automáticas
  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      setError("Access token is not available");
      setIsLoading(false);
      return;
    }

    const allRFCs = availableRFCs.map(rfc => rfc.value);
    const weekDates = getWeekDates();
    
    const params = {
      fechaInicio: weekDates.fechaInicio,
      fechaFin: weekDates.fechaFin,
      customer_rfc: allRFCs
    };

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/customer/reports/paid/weekly`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          }, 
          params: params 
        }
      );

      const transformedData = response.data.map((item, index) => {
        const formatFecha = (fechaData) => {
          if (!fechaData) return '-';
          if (typeof fechaData === 'object' && fechaData.value) {
            return FormatLocalDateStringBorder(fechaData.value);
          }
          return FormatLocalDateStringBorder(fechaData);
        };

        return {
          id: index,
          CLIENTE: item.RAZONSOCIALPEDIMENTO || '-',
          PLAZA: item.ADUANADESCRIPCION || '-',
          OPERACION: item.TIPO_OPERACION || '-',
          FECHA: formatFecha(item.FECHAPAGO),
          ADUANA: item.ADUANA || '-',
          PATENTE: item.PATENTE || '-',
          PEDIMENTO: item.PEDIMENTO || '-',
          REFERENCIA: item.REFERENCIA_PEDIMENTO || '-',
          SUMA: item.TOTAL_IMPUESTOS_PEDIMENTO || '-',
          IMPUESTOS_PAGADOS: item.BANCO_PEDIMENTO || '-',
          PLANTA: determinePlanta(item.RFCCLIENTEPEDIMENTO, item.BODDESTINO),
        };
      });

      setAllItems(transformedData);
      setTotalPages(Math.ceil(transformedData.length / pageSize));
      setIsLoading(false);
    } catch (error) {
      setError("Failed to fetch data");
      setIsLoading(false);
      console.error("Failed to fetch data:", error);
    }
  };

  const renderTable = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: 'CLIENTE', label: 'CLIENTE' },
                { key: 'PLAZA', label: 'PLAZA' },
                { key: 'OPERACION', label: 'OPERACION' },
                { key: 'FECHA', label: 'FECHA' },
                { key: 'ADUANA', label: 'ADUANA' },
                { key: 'PATENTE', label: 'PATENTE' },
                { key: 'PEDIMENTO', label: 'PEDIMENTO' },
                { key: 'REFERENCIA', label: 'REFERENCIA' },
                { key: 'SUMA', label: 'SUMA' },
                { key: 'IMPUESTOS_PAGADOS', label: 'IMPUESTOS PAGADOS' },
                { key: 'PLANTA', label: 'PLANTA' },
              ].map(({ key, label }) => (
                <th 
                  key={key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => requestSort(key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{label}</span>
                    {sortConfig.key === key && (
                      <FontAwesomeIcon 
                        icon={sortConfig.direction === 'ascending' ? faChevronDown : faChevronRight} 
                        className="ml-1 text-xs" 
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.length > 0 ? items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.CLIENTE}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.PLAZA}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.OPERACION}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.FECHA}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.ADUANA}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.PATENTE}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.PEDIMENTO}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.REFERENCIA}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.SUMA}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.IMPUESTOS_PAGADOS}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.PLANTA}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="11" className="px-6 py-4 text-center text-sm text-gray-500">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPagination = () => (
    <Pagination
      total={totalPages * pageSize}
      pageSize={pageSize}
      currentPage={currentPage}
      onPageChange={(page) => setCurrentPage(page)}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setCurrentPage(1);
      }}
    />
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-xl font-semibold animate-spin"
        />
        <div className="text-xl font-semibold ml-2">Loading...</div>
      </div>
    );
  }
  
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      {renderHeader()}
      <div
        className="container mx-auto px-4 sm:px-8 py-8 bg-gray-100"
        style={{ maxWidth: "100%" }}
      >
        {renderSelectFilters()}

        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
          </div>
          <ExportCSVButton data={csvData}  filename={`Reporte_Pedimentos_Pagados_${formattedDate}.csv`} />
        </div>

        {renderTable()}
        <div className="mt-6">
          {renderPagination()}
        </div>
      </div>
    </>
  );
};

export default CustomerReportWeeklyOperations;